import React, { useState, useEffect } from "react";
import { Plus } from "lucide-react";
import SlideOutPanel from "./SlideOutPanel";
import { TextInput, TextArea } from "../forms/FormFields";

export const AddPlayerPanel = ({
  isOpen,
  onClose,
  onSubmit,
  availablePlayers = [],
  isPitcher = false,
}) => {
  const [mode, setMode] = useState("existing");
  const [selectedPlayer, setSelectedPlayer] = useState("");
  const [formData, setFormData] = useState({
    playerId: null,
    name: "",
    position: "",
    writeup: "",
  });

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  const resetForm = () => {
    setMode("existing");
    setSelectedPlayer("");
    setFormData({
      playerId: null,
      name: "",
      position: "",
      writeup: "",
    });
  };

  const handleExistingPlayerSelect = (e) => {
    const player = availablePlayers.find((p) => p.name === e.target.value);
    if (player) {
      setSelectedPlayer(player.name);
      setFormData({
        playerId: player.playerId,
        name: player.name,
        position: player.position || "",
        writeup: "",
      });
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
    resetForm();
    onClose();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const playerType = isPitcher ? "Pitcher" : "Player";

  return (
    <SlideOutPanel
      isOpen={isOpen}
      onClose={handleClose}
      title={`Add ${playerType}`}
      width="w-[500px]"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Mode Toggle */}
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            type="button"
            onClick={() => setMode("existing")}
            className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
              mode === "existing"
                ? "bg-white text-blue-600 shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            Select Existing
          </button>
          <button
            type="button"
            onClick={() => setMode("new")}
            className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
              mode === "new"
                ? "bg-white text-blue-600 shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            Create New
          </button>
        </div>

        {/* Existing Player Selector */}
        {mode === "existing" && (
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-gray-700">
              Choose a {playerType.toLowerCase()}
            </label>
            <select
              value={selectedPlayer}
              onChange={handleExistingPlayerSelect}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            >
              <option value="">Select a {playerType.toLowerCase()}...</option>
              {availablePlayers.map((player) => (
                <option key={player.playerId} value={player.name}>
                  {player.name} - {player.position}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Name & Position */}
        <TextInput
          label={`${playerType} Name`}
          value={formData.name}
          onChange={(v) => handleChange("name", v)}
          disabled={mode === "existing" && selectedPlayer}
          placeholder={`Enter ${playerType.toLowerCase()} name`}
        />

        <TextInput
          label={isPitcher ? "Role" : "Position"}
          value={formData.position}
          onChange={(v) => handleChange("position", v)}
          disabled={mode === "existing" && selectedPlayer}
          placeholder={isPitcher ? "e.g., Starter, Reliever" : "e.g., SS, CF"}
        />

        {/* Initial Writeup (optional) */}
        <TextArea
          label="Initial Notes (optional)"
          value={formData.writeup}
          onChange={(v) => handleChange("writeup", v)}
          rows={3}
          placeholder="Add any initial notes..."
        />

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!formData.name}
            className={`flex-1 px-4 py-2 text-white rounded-lg transition-colors font-medium flex items-center justify-center gap-2 ${
              isPitcher
                ? "bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300"
                : "bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300"
            }`}
          >
            <Plus size={16} />
            Add {playerType}
          </button>
        </div>
      </form>
    </SlideOutPanel>
  );
};
